<!--
  PseudoNotification — the simulated push banner (#106).

  Faithful port of app-salon-tools' PseudoNotificaction.vue (commit 205b6630;
  the original filename's typo is not ported): an in-app banner styled like a
  native OS notification, for the surface where a real push cannot exist —
  the vitrina has no service worker on purpose (Planning §2.3). Class names
  kept as the original's so the SCSS port stays diffable against the source.

  This component owns the display timing: the 5s auto-expiry runs the SAME
  leave animation a tap runs (Codex — the composable dismissing on its own
  timer made expiry visually abrupt). A tap runs the notification's action;
  expiry never does. The exit is idempotent so a touchmove gesture cannot
  re-fire the action while the banner is already leaving.
-->
<template>
  <div
    v-if="active"
    class="pseudo-notificaction-container"
    role="alert"
    aria-live="assertive"
  >
    <div
      class="pseudo-notification-base"
      :class="exitClass"
      tabindex="0"
      data-test="pseudo-notification"
      @click="close(true)"
      @touchmove="close(true)"
      @keydown.escape="close(false)"
      @keydown.enter="close(true)"
    >
      <div class="pseudo-notification">
        <img
          src="@/assets/svgLogos/iso-salon-color.svg"
          alt=""
          aria-hidden="true"
          class="svg-logo"
        >
        <div class="pseudo-notification-title">
          {{ active.title || "Salon" }}
        </div>
        <div class="pseudo-notification-body">
          {{ active.body }}
        </div>
        <div
          class="pseudo-notification-now"
          aria-hidden="true"
        >
          {{ $t('customerMenu.notification_now') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onUnmounted, ref, watch } from 'vue';
import { usePseudoNotification } from '@/js/composables/usePseudoNotification';

const TIMEOUT_MS = 5000; // the original pseudo-notification default
const EXIT_MS = 400; // matches pseudo-slide-up's 0.4s

const { active, dismiss } = usePseudoNotification();
const exitClass = ref('');
let expiryTimer = null;
let exitTimer = null;

// `withAction` distinguishes a deliberate tap/Enter (run the notification's
// action) from expiry/Escape (just leave). Idempotent: once the banner is
// leaving, further events — a touchmove stream included — do nothing.
function close(withAction) {
  if (exitClass.value) return;
  if (expiryTimer) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
  exitClass.value = 'leave-active';
  if (withAction && active.value?.onClickAction) {
    active.value.onClickAction();
  }
  exitTimer = setTimeout(() => {
    exitClass.value = '';
    exitTimer = null;
    dismiss(); // the queue advances; the watcher re-arms for the next one
  }, EXIT_MS);
}

watch(
  active,
  (current) => {
    if (expiryTimer) {
      clearTimeout(expiryTimer);
      expiryTimer = null;
    }
    if (current) {
      expiryTimer = setTimeout(() => close(false), TIMEOUT_MS);
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  if (expiryTimer) clearTimeout(expiryTimer);
  if (exitTimer) clearTimeout(exitTimer);
});
</script>
